// "Connect wallets" page — one row per chain. Each row shows the
// chain icon + label, the connected account (or "Not connected"), and
// a button that triggers the chain-specific connect flow. Bottom of
// the page surfaces a trusted-relayer probe against /api/check-relayer.

import { useEffect, useMemo, useState } from "react";
import { friendlyWalletError } from "@/lib/walletError";
import { useConnect as useEvmConnect, useAccount as useEvmAccount, useDisconnect as useEvmDisconnect } from "wagmi";
import { useWallet as useSolWallet } from "@solana/wallet-adapter-react";
import { WalletReadyState } from "@solana/wallet-adapter-base";
import { useAccount as useStarkAccount, useConnect as useStarkConnect, useDisconnect as useStarkDisconnect } from "@starknet-react/core";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { listConnectors, checkRelayer, ApiClientError } from "@/api/client";
import { useNearWallet } from "@/wallet/near";
import { useBtcWallet } from "@/wallet/btc";
import { truncateMiddle } from "@/lib/truncate";
import { KIND_ICON } from "@/components/icons";
import type { ConnectorInfo, RelayerStatus } from "@/types/dto";

export function ConnectPage() {
  return (
    <section className="space-y-8" data-testid="connect-page">
      <header className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-slate-100">
          Connect wallets
        </h1>
        <p className="max-w-3xl text-sm text-slate-600 dark:text-slate-400">
          Authorise each chain whose wallet you'll need this session. NEAR
          signs OmniBridge Ops calls on NEAR (omni-bridge, btc-connector,
          zcash-connector); each other wallet signs its own chain-side
          bridge operations.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <NearRow />
        <EvmRow />
        <SolanaRow />
        <BitcoinRow />
        <StarknetRow />
        <ZcashRow />
      </div>

      <RelayerStatusCard />
    </section>
  );
}

// ─── Rows ───────────────────────────────────────────────────────────────

function ChainRow(props: {
  kind: string;
  label: string;
  installable?: string;
  available: boolean;
  account: string | null;
  onConnect: () => void;
  onDisconnect?: () => void;
  pendingPicker?: React.ReactNode;
}) {
  const { kind, label, installable, available, account, onConnect, onDisconnect, pendingPicker } = props;
  const Icon = KIND_ICON[kind];
  return (
    <div
      className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4"
      data-testid={`wallet-row-${kind}`}
      data-wallet-kind={kind}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          {Icon && <Icon className="h-8 w-8 shrink-0" />}
          <div className="min-w-0">
            <div className="text-slate-900 dark:text-slate-100 font-medium">{label}</div>
            <div className="mt-0.5 text-sm font-mono text-slate-600 dark:text-slate-400 truncate">
              {account ? truncateMiddle(account, 8, 6) : "Not connected"}
            </div>
          </div>
        </div>
        {account ? (
          onDisconnect && (
            <button
              type="button"
              onClick={onDisconnect}
              className="rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1 text-xs font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Disconnect
            </button>
          )
        ) : available ? (
          <button
            type="button"
            onClick={onConnect}
            className="rounded-md bg-near-green px-3 py-1 text-xs font-semibold text-slate-950 hover:bg-near-green-dark"
          >
            Connect
          </button>
        ) : (
          <span className="rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1 text-xs font-medium text-slate-600 dark:text-slate-400">
            Wallet not detected
          </span>
        )}
      </div>
      {!account && !available && installable && (
        <div className="mt-2 text-xs text-slate-500">
          <span className="text-slate-600 dark:text-slate-400">Install one of:</span> {installable}
        </div>
      )}
      {pendingPicker}
    </div>
  );
}

function NearRow() {
  const w = useNearWallet();
  return (
    <ChainRow
      kind="near"
      label="NEAR"
      available
      account={w.account}
      onConnect={() => void w.connect()}
      onDisconnect={() => void w.disconnect()}
    />
  );
}

function EvmRow() {
  const { address, isConnected, connector } = useEvmAccount();
  const { connectAsync, connectors } = useEvmConnect();
  const { disconnectAsync } = useEvmDisconnect();
  const [pendingPick, setPendingPick] = useState(false);

  const ready = connectors.length > 0;
  const handleConnect = async () => {
    if (connectors.length === 1) {
      await connectAsync({ connector: connectors[0]! });
      return;
    }
    setPendingPick(true);
  };

  const picker = pendingPick ? (
    <div className="mt-3 space-y-1">
      {connectors.map((c) => (
        <button
          type="button"
          key={c.uid}
          onClick={async () => {
            setPendingPick(false);
            await connectAsync({ connector: c });
          }}
          className="block w-full rounded-md border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-1 text-left text-xs text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
        >
          {c.name}
        </button>
      ))}
      <button
        type="button"
        onClick={() => setPendingPick(false)}
        className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
      >
        Cancel
      </button>
    </div>
  ) : connector ? (
    <div className="mt-2 text-xs text-slate-500">via {connector.name}</div>
  ) : null;

  return (
    <ChainRow
      kind="evm"
      label="EVM"
      installable="MetaMask, Rabby, OKX Wallet, Coinbase Wallet, or any EIP-1193 provider"
      available={ready}
      account={isConnected ? (address ?? null) : null}
      onConnect={() => void handleConnect()}
      onDisconnect={() => void disconnectAsync()}
      pendingPicker={picker}
    />
  );
}

function SolanaRow() {
  const { publicKey, disconnect, wallets, select, connect, wallet, connecting } = useSolWallet();
  const account = publicKey?.toBase58() ?? null;
  const [pick, setPick] = useState(false);
  const [pendingName, setPendingName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const installed = wallets.filter(
    (w) => w.readyState === WalletReadyState.Installed || w.readyState === WalletReadyState.Loadable,
  );

  // wallet-adapter-react flow:
  //   1. We call `select(name)` to nominate a wallet.
  //   2. WalletProvider mutates its internal state; on the next render
  //      `wallet?.adapter.name === name`.
  //   3. An effect (ours, below) sees the adapter is now in place
  //      and calls `connect()`. We do NOT rely on `autoConnect={true}`
  //      to trigger this because autoConnect only fires on adapter
  //      `readyStateChange` (re-)events, not on a fresh `select()` —
  //      Solflare in particular sits in `Loadable` for the first tick
  //      after the page loads and autoConnect never re-fires.
  useEffect(() => {
    if (!pendingName) return;
    if (!wallet || wallet.adapter.name !== pendingName) return;
    // Capture the adapter to detect a `select(other)` happening mid-flight.
    const adapter = wallet.adapter;
    let cancelled = false;
    setError(null);
    void (async () => {
      try {
        await connect();
      } catch (e) {
        if (cancelled || wallet.adapter !== adapter) return;
        setError(friendlyWalletError(e));
      } finally {
        if (!cancelled) setPendingName(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pendingName, wallet, connect]);

  const doConnect = (walletName: string) => {
    setError(null);
    select(walletName as Parameters<typeof select>[0]);
    setPendingName(walletName);
  };

  const picker = pick ? (
    <div className="mt-3 space-y-1">
      {installed.map((w) => (
        <button
          key={w.adapter.name}
          type="button"
          onClick={() => {
            setPick(false);
            doConnect(w.adapter.name);
          }}
          className="block w-full rounded-md border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-1 text-left text-xs text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
        >
          {w.adapter.name}
        </button>
      ))}
      <button
        type="button"
        onClick={() => setPick(false)}
        className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
      >
        Cancel
      </button>
    </div>
  ) : error ? (
    <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>
  ) : connecting || pendingName ? (
    <div className="mt-2 text-xs text-slate-500">connecting…</div>
  ) : wallet ? (
    <div className="mt-2 text-xs text-slate-500">via {wallet.adapter.name}</div>
  ) : null;

  return (
    <ChainRow
      kind="sol"
      label="Solana"
      installable="Phantom, Backpack, Solflare, or any Wallet-Standard wallet"
      available={installed.length > 0}
      account={account}
      onConnect={() => {
        if (installed.length === 1) {
          doConnect(installed[0]!.adapter.name);
        } else {
          setPick(true);
        }
      }}
      onDisconnect={() => void disconnect()}
      pendingPicker={picker}
    />
  );
}

function BitcoinRow() {
  const w = useBtcWallet();
  const [pick, setPick] = useState(false);
  const picker = pick ? (
    <div className="mt-3 space-y-1">
      {w.providers.map((p) => (
        <button
          key={p.id}
          type="button"
          onClick={() => {
            setPick(false);
            void w.connect(p.id);
          }}
          className="block w-full rounded-md border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-1 text-left text-xs text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700"
        >
          {p.name}
        </button>
      ))}
      <button
        type="button"
        onClick={() => setPick(false)}
        className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
      >
        Cancel
      </button>
    </div>
  ) : null;
  return (
    <ChainRow
      kind="btc"
      label="Bitcoin"
      installable="Unisat, Leather (Hiro), OKX Wallet, Xverse, Phantom (BTC enabled)"
      available={w.available}
      account={w.account}
      onConnect={() => (w.providers.length > 1 ? setPick(true) : void w.connect())}
      onDisconnect={() => void w.disconnect()}
      pendingPicker={picker}
    />
  );
}

function StarknetRow() {
  const { address } = useStarkAccount();
  const { connectAsync, connectors, isPending } = useStarkConnect();
  const { disconnect } = useStarkDisconnect();
  const [pick, setPick] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Starknet connectors are configured up front (Argent X + Braavos), so
  // `connectors.length` is always 2 — that's not a real availability
  // signal. Filter by `connector.available()`, which checks for the
  // `globalThis.starknet_<id>` injection the extension sets up.
  const installed = useMemo(() => connectors.filter((c) => c.available()), [connectors]);

  const doConnect = async (connector: (typeof connectors)[number]) => {
    setError(null);
    try {
      await connectAsync({ connector });
    } catch (e) {
      setError(friendlyWalletError(e));
    }
  };

  const picker = pick ? (
    <div className="mt-3 space-y-1">
      {installed.map((c) => (
        <button
          key={c.id}
          type="button"
          disabled={isPending}
          onClick={() => {
            setPick(false);
            void doConnect(c);
          }}
          className="block w-full rounded-md border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800 px-3 py-1 text-left text-xs text-slate-800 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-700 disabled:opacity-50"
        >
          {c.name}
        </button>
      ))}
      <button
        type="button"
        onClick={() => setPick(false)}
        className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300"
      >
        Cancel
      </button>
    </div>
  ) : error ? (
    <div className="mt-2 text-xs text-red-700 dark:text-red-300">{error}</div>
  ) : isPending ? (
    <div className="mt-2 text-xs text-slate-500">connecting…</div>
  ) : null;

  return (
    <ChainRow
      kind="starknet"
      label="Starknet"
      installable="Ready (Argent X) or Braavos"
      available={installed.length > 0}
      account={address ?? null}
      onConnect={() => {
        setError(null);
        if (isPending) return;
        if (installed.length === 1) {
          void doConnect(installed[0]!);
        } else {
          setPick(true);
        }
      }}
      onDisconnect={() => disconnect()}
      pendingPicker={picker}
    />
  );
}

function ZcashRow() {
  // OmniBridge Ops doesn't need an operator-side Zcash wallet for any
  // current flow:
  //   - DEPOSITS land at a transparent t-address (`tm…` on testnet,
  //     `t1…`/`t3…` on mainnet) that the bridge issues per recipient.
  //     The user signs upstream with their own Zcash wallet — Zashi,
  //     ywallet, zcashd-rpc, Edge, etc. — converting shielded balances
  //     to transparent on-the-fly if needed.
  //   - WITHDRAWALS produce a Zcash transparent transaction via NEAR
  //     chain-signature MPC; the operator triggers it from the NEAR
  //     wallet (`Sign UTXO withdraw on NEAR`), never signs Zcash bytes.
  // Bitcoin wallets can NOT sign Zcash transactions — different
  // consensus, different sighash (NU5 ZIP-244), different address
  // formats including Sapling + Orchard shielded pools.
  const Icon = KIND_ICON["zcash"];
  return (
    <div
      className="rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4"
      data-testid="wallet-row-zcash"
      data-wallet-kind="zcash"
    >
      <div className="flex items-start gap-3">
        {Icon && <Icon className="h-8 w-8 shrink-0" />}
        <div>
          <div className="text-slate-900 dark:text-slate-100 font-medium">Zcash</div>
          <div className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">
            No operator wallet needed. Deposits land at a transparent
            <code className="mx-1 rounded bg-slate-100 dark:bg-slate-800 px-1 font-mono text-[11px] text-slate-800 dark:text-slate-200">
              t-address
            </code>
            and are signed upstream by the user (Zashi / ywallet / zcashd);
            withdrawals are produced by NEAR chain-signature MPC, not a
            Zcash wallet on this side.
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Trusted-relayer status ─────────────────────────────────────────────

function RelayerStatusCard() {
  const near = useNearWallet();
  const [account, setAccount] = useState("");
  const [submitted, setSubmitted] = useState<string | null>(null);
  const connectorsQ = useQuery({
    queryKey: ["connectors"],
    queryFn: ({ signal }) => listConnectors(signal),
    staleTime: 60_000,
  });
  const probeQ = useQuery({
    queryKey: ["check-relayer", submitted],
    queryFn: ({ signal }) => checkRelayer(submitted!, signal),
    enabled: !!submitted,
  });

  // Auto-submit the relayer probe the moment a NEAR wallet connects —
  // matches the Leptos behaviour where the status appeared without
  // typing. Only fires when the operator hasn't typed something custom
  // into the input (so an explicit "check alice.testnet" doesn't get
  // overwritten by `near.account` later).
  useEffect(() => {
    if (!near.account) return;
    if (account.trim()) return;
    setSubmitted(near.account);
  }, [near.account, account]);

  const placeholder = near.account ?? "alice.testnet";
  const effective = useMemo(() => account.trim() || near.account || "", [account, near.account]);

  const onCheck = () => {
    if (!effective) return;
    // If the operator is re-checking the same account the auto-effect
    // already submitted, `setSubmitted(effective)` would be a no-op
    // (React short-circuits identical primitive setState) and the
    // query key wouldn't change → no refetch. Call `refetch()`
    // explicitly so the click always hits the network.
    if (effective === submitted) {
      void probeQ.refetch();
    } else {
      setSubmitted(effective);
    }
  };

  return (
    <section className="mt-8 space-y-3" data-testid="relayer-card">
      <header className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Trusted-relayer status</h2>
          <p className="text-xs text-slate-500">
            Soft check only. The contract is the real enforcement point — submission is allowed either way.
          </p>
        </div>
        <button
          type="button"
          onClick={onCheck}
          disabled={!effective || probeQ.isFetching}
          className="rounded-md border border-slate-300 dark:border-slate-700 px-3 py-1 text-xs font-medium text-slate-800 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40"
          data-testid="relayer-check"
        >
          {probeQ.isFetching ? "Checking…" : "Check"}
        </button>
      </header>
      <input
        type="text"
        value={account}
        onChange={(e) => setAccount(e.target.value)}
        placeholder={placeholder}
        aria-label="Relayer account id"
        className="w-full rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm font-mono text-slate-900 dark:text-slate-100 placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:border-near-green focus:outline-none"
      />
      {probeQ.error instanceof ApiClientError && (
        <div className="rounded-md border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/40 px-3 py-2 text-xs text-red-700 dark:text-red-200" data-testid="relayer-error">
          {probeQ.error.message}
        </div>
      )}
      {probeQ.data && (
        <div className="space-y-1">
          {(connectorsQ.data ?? []).map((c) => (
            <RelayerRow
              key={c.id}
              connector={c}
              status={probeQ.data!.per_connector[c.id]}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function RelayerRow({
  connector,
  status,
}: {
  connector: ConnectorInfo;
  status: RelayerStatus | undefined;
}) {
  return (
    <div
      className="flex items-center justify-between rounded-md border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-2"
      data-testid={`relayer-row-${connector.id}`}
    >
      <div>
        <div className="text-sm text-slate-800 dark:text-slate-200">{connector.label}</div>
        <div className="font-mono text-xs text-slate-500">{connector.contract}</div>
      </div>
      <span
        className={clsx(
          "rounded-md px-2 py-0.5 text-[11px] font-medium border whitespace-nowrap",
          status?.kind === "trusted"
            ? "bg-near-green/15 text-near-green-dark dark:text-near-green border-near-green/40"
            : status?.kind === "pending"
              ? "bg-amber-50 dark:bg-amber-900/30 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-800/60"
              : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border-slate-300 dark:border-slate-700",
        )}
      >
        {status?.kind === "trusted"
          ? "Trusted"
          : status?.kind === "pending"
            ? "Pending"
            : "Not trusted"}
      </span>
    </div>
  );
}
